import { Metamodel } from 'app/model/base/model';

import { StoreReadSettings } from './store-read-settings';
import { Dataset } from 'app/model/dataset/dataset';
import { FormatDatasetV2 } from 'app/model/dataset/dataset.v2';
import { AvroDataset, azureStorageAuthTypes, compressionCodecType, DelimitedTextDataset, IModel, IAttribute, IViolation, JsonDataset, ParquetDataset } from 'shell-models-lib';
import { OrcDataset } from 'app/model/dataset/orc.dataset';
import { FilePathMode } from 'app/registries/consts/metadata-consts';
import { CopyActivity } from '../../copy.activity';
import { ExcelDataset } from 'app/model/dataset/excel.dataset';
import { isBinaryTypeDataset, datasetWithExpectedCompressionType } from 'app/shared/utils/dataset-validation.util';
import { format } from 'app/shared/utils/string.util';
import { AzureBlobStorageLinkedService } from 'app/model/linkedservice/azure-blob-storage.linkedservice';

@Metamodel.model({
    abstract: true
})

export abstract class BlobTypeStoreReadSettings extends StoreReadSettings {
    public static readonly MODEL: IModel;
    public static readonly ENABLE_PARTITION_DISCOVERY: IAttribute;
    public static readonly PARTITION_ROOT_PATH: IAttribute;
    public static readonly MODIFID_DATETIME_START: IAttribute;
    public static readonly MODIFID_DATETIME_END: IAttribute;
    public static readonly RECURSIVE_SETTING: IAttribute;
    public static readonly DELETE_FILE_AFTER_COMPLETION: IAttribute;
    public static readonly WILDCARD_FOLDERPATH: IAttribute;
    public static readonly WILDCARD_FILENAME: IAttribute;
    public static readonly FILE_LIST_PATH: IAttribute;
    public static readonly FILEPATH_MODE: IAttribute;
    public static readonly PREFIX: IAttribute;

    // overwrite in all sub class
    public supportedFilePathMode: FilePathMode[] = [];

    @Metamodel.attribute({
        templatable: true,
        association: 'RECURSIVE_SETTING',
        display: stringResources.Recursive_Copy_Label
    })
    public recursive: boolean = true;

    @Metamodel.attribute({
        templatable: true,
        association: 'DELETE_FILE_AFTER_COMPLETION',
        display: stringResources.DeleteFilesAfterCompletion_Label,
        constraint: {
            validator: BlobTypeStoreReadSettings.validateDeleteFilesAfterCompletion
        }
    })
    public deleteFilesAfterCompletion?: boolean;

    @Metamodel.attribute({
        association: 'FILEPATH_MODE',
        display: stringResources.FilePathType,
        constraint: {
            validator: BlobTypeStoreReadSettings.validateFilePathMode
        }
    })
    public filePathMode: FilePathMode = FilePathMode.DATASET;

    @Metamodel.attribute({
        display: stringResources.ModelName_WildcardFolderPath,
        association: 'WILDCARD_FOLDERPATH',
        templatable: true
    })
    public wildcardFolderPath?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_WildcardFileName,
        association: 'WILDCARD_FILENAME',
        templatable: true
    })
    public wildcardFileName?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_FileListPath,
        association: 'FILE_LIST_PATH',
        templatable: true
    })
    public fileListPath?: string;

    @Metamodel.attribute({
        display: stringResources.EnablePartitionDiscovery,
        association: 'ENABLE_PARTITION_DISCOVERY',
        templatable: true
    })
    public enablePartitionDiscovery?: boolean = false;

    @Metamodel.attribute({
        display: stringResources.DataFlow_PartitionRootPath,
        association: 'PARTITION_ROOT_PATH',
        templatable: true
    })
    public partitionRootPath?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_ModifiedDatetimeStart,
        association: 'MODIFID_DATETIME_START',
        templatable: true
    })
    public modifiedDatetimeStart?: Date;

    @Metamodel.attribute({
        display: stringResources.ModelName_ModifiedDatetimeEnd,
        association: 'MODIFID_DATETIME_END',
        templatable: true
    })
    public modifiedDatetimeEnd?: Date;

    public static validateFilePathMode(storeSettings: BlobTypeStoreReadSettings): IViolation[] | undefined {
        if (storeSettings.filePathMode === FilePathMode.LIST) {
            const sourceDataset = (storeSettings.owner.owner as CopyActivity).sourceDataset as FormatDatasetV2;
            if (sourceDataset != null && sourceDataset.location != null && !sourceDataset.location.isFolder()) {
                return [
                    {
                        context: storeSettings.filePathMode,
                        message: () => format(stringResources.fileListPathInCompatibleWithFileName, stringResources.ModelName_FileListPath)
                    }
                ];
            }
        } else if (storeSettings.filePathMode === FilePathMode.NameRange) {
            const sourceDataset = (storeSettings.owner.owner as CopyActivity).sourceDataset as FormatDatasetV2;
            if (sourceDataset != null && sourceDataset.location != null && !sourceDataset.location.isFolder()) {
                return [
                    {
                        context: storeSettings.filePathMode,
                        message: () => format(stringResources.fileListPathInCompatibleWithFileName, stringResources.ModelName_ListBeofreAndAfterMode)
                    }
                ];
            }
        }
        return undefined;
    }

    public static validateDeleteFilesAfterCompletion(storeSettings: BlobTypeStoreReadSettings): IViolation[] | undefined {
        if (storeSettings.deleteFilesAfterCompletion) {
            const violations: IViolation[] = [];
            const sourceDataset = (storeSettings.owner.owner as CopyActivity).sourceDataset;
            const sinkDataset = (storeSettings.owner.owner as CopyActivity).sinkDataset;
            // source and sink must be binary dataset, and not specify compression type
            if (sourceDataset && sinkDataset) {
                if (!isBinaryTypeDataset(sourceDataset) || !isBinaryTypeDataset(sinkDataset) ||
                    !(datasetWithExpectedCompressionType(sourceDataset, undefined) || datasetWithExpectedCompressionType(sourceDataset, '')
                        || datasetWithExpectedCompressionType(sourceDataset, compressionCodecType.none)) ||
                    !(datasetWithExpectedCompressionType(sinkDataset, undefined) ||
                        datasetWithExpectedCompressionType(sinkDataset, '') || datasetWithExpectedCompressionType(sinkDataset, compressionCodecType.none))) {
                    violations.push(
                        {
                            context: storeSettings.deleteFilesAfterCompletion,
                            message: () => stringResources.Binary_Copy_DeleteFilesAfterCompletionError
                        });

                }
                if (sourceDataset.linkedService instanceof AzureBlobStorageLinkedService &&
                    sourceDataset.linkedService.authenticationMethod === azureStorageAuthTypes.Anonymous) {
                    return [{
                        message: () => stringResources.Binary_Copy_DeleteFilesAfterCompletionAnonymousError
                    }];
                }
                return violations;
            }
        }
        return undefined;
    }

    public init(dataset: Dataset): void {
        if (dataset instanceof FormatDatasetV2 && dataset.location && dataset.location.isFolder() && !this.wildcardFileName) {
            if (dataset instanceof DelimitedTextDataset) {
                this.wildcardFileName = '*';
            }
            if (dataset instanceof ParquetDataset) {
                this.wildcardFileName = '*.parquet';
            }
            if (dataset instanceof AvroDataset) {
                this.wildcardFileName = '*.avro';
            }
            if (dataset instanceof JsonDataset) {
                this.wildcardFileName = '*.json';
            }
            if (dataset instanceof OrcDataset) {
                this.wildcardFileName = '*.orc';
            }
            if (dataset instanceof ExcelDataset) {
                this.wildcardFileName = '*'; // synced up with backend sme, excel format type may be equipped with difference suffix
            }
        }
    }

    public recoverPropFromPrevOne(prevStoreSettings: StoreReadSettings): void {
        if (prevStoreSettings instanceof BlobTypeStoreReadSettings) {
            this.updateModelValue('enablePartitionDiscovery', this, prevStoreSettings);
            this.updateModelValue('partitionRootPath', this, prevStoreSettings);
            this.updateModelValue('modifiedDatetimeStart', this, prevStoreSettings);
            this.updateModelValue('modifiedDatetimeEnd', this, prevStoreSettings);
            this.updateModelValue('recursive', this, prevStoreSettings);
            this.updateModelValue('maxConcurrentConnections', this, prevStoreSettings);
            this.updateModelValue('wildcardFolderPath', this, prevStoreSettings);
            this.updateModelValue('wildcardFileName', this, prevStoreSettings);
            if (prevStoreSettings.filePathMode != null && this.supportedFilePathMode && this.supportedFilePathMode.indexOf(prevStoreSettings.filePathMode) !== -1) {
                this.filePathMode = prevStoreSettings.filePathMode;
            }
            this.updateModelValue('fileListPath', this, prevStoreSettings);
        } else {
            this.updateModelValue('maxConcurrentConnections', this, prevStoreSettings);
        }
    }
}
